import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShieldCheck, 
  ArrowRight, 
  CheckCircle2, 
  BarChart3, 
  FileText, 
  Sparkles,
  ClipboardCheck,
  Users,
  Building2,
  Award,
  Play,
  MessageCircle,
  Video,
  Clock,
  Star,
  ChevronRight,
  Zap,
  LineChart,
  AlertTriangle,
  BookOpen,
  Send,
  Menu,
  X,
  Circle
} from "lucide-react";

// Compliance trust logos
const trustBadges = [
  { name: "CMS", subtitle: "Centers for Medicare & Medicaid" },
  { name: "TJC", subtitle: "The Joint Commission" },
  { name: "CDC", subtitle: "Centers for Disease Control" },
  { name: "APIC", subtitle: "Association for Professionals in IC" },
  { name: "NHSN", subtitle: "National Healthcare Safety Network" },
  { name: "OSHA", subtitle: "Occupational Safety & Health" }
];

// IP Consultants for Live Chat section
const ipConsultants = [
  { 
    name: "Dr. Sarah Chen, CIC®, MPH", 
    specialty: "Acute Care HAI Prevention",
    image: "https://images.unsplash.com/photo-1673865641073-4479f93a7776?w=150&h=150&fit=crop&crop=face",
    status: "online"
  },
  { 
    name: "Marcus Williams, CIC®", 
    specialty: "Long-Term Care Compliance",
    image: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=150&h=150&fit=crop&crop=face",
    status: "online"
  },
  { 
    name: "Dr. Priya Patel, CIC®, PhD", 
    specialty: "Outbreak Investigation",
    image: "https://images.unsplash.com/photo-1612349316228-5942a9b489c2?w=150&h=150&fit=crop&crop=face",
    status: "busy"
  }
];

// Features for bento grid
const bentoFeatures = [
  { 
    id: "mock-survey",
    icon: ClipboardCheck, 
    title: "Mock Survey Generator", 
    description: "Simulate CMS/Joint Commission surveys with AI-generated tracer questions",
    size: "large",
    color: "emerald"
  },
  { 
    id: "ai-docs",
    icon: FileText, 
    title: "AI Document Engine", 
    description: "Generate policies, training materials, and compliance documents instantly",
    size: "medium",
    color: "mint"
  },
  { 
    id: "scorecard",
    icon: BarChart3, 
    title: "Infection Scorecard", 
    description: "Track CAUTI, CLABSI, SSI, and hand hygiene in real-time",
    size: "medium",
    color: "emerald"
  },
  { 
    id: "outbreak",
    icon: AlertTriangle, 
    title: "Outbreak Heatmap", 
    description: "Visualize infection clusters and transmission patterns",
    size: "small",
    color: "amber"
  },
  { 
    id: "multi-tenant",
    icon: Building2, 
    title: "Multi-Facility Sync", 
    description: "Manage multiple locations from one dashboard",
    size: "small",
    color: "slate"
  },
  { 
    id: "education",
    icon: BookOpen, 
    title: "Staff Education Hub", 
    description: "AI-generated training with auto-generated signage",
    size: "medium",
    color: "mint"
  }
];

// Pricing plans
const pricingPlans = [
  { 
    name: "Essentials", 
    monthlyPrice: 49,
    yearlyPrice: 39,
    description: "For small facilities getting started",
    features: ["5 team members", "500 documents/month", "Basic scorecard", "Email support", "10 AI generations/day"],
    cta: "Start Free Trial"
  },
  { 
    name: "Professional", 
    monthlyPrice: 99,
    yearlyPrice: 79,
    description: "Full-featured for dedicated IPs",
    features: ["20 team members", "2,000 documents/month", "Mock surveys", "Advanced analytics", "Priority support", "50 AI generations/day", "Live IP chat (5 hrs/mo)"],
    popular: true,
    cta: "Start Free Trial"
  },
  { 
    name: "Enterprise", 
    monthlyPrice: 249,
    yearlyPrice: 199,
    description: "For health systems & networks",
    features: ["Unlimited team members", "Unlimited documents", "Custom integrations", "API access", "Dedicated support", "Unlimited AI", "Unlimited live IP access", "White-label option"],
    cta: "Contact Sales"
  }
];

// Custom Shield+Pulse Logo Component
const NikosafeLogo = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3L4 7v6c0 5.5 3.5 10 8 11 4.5-1 8-5.5 8-11V7l-8-4z" />
    <path d="M6 12h4l2-3 2 5 2-2h4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Testimonials
const testimonials = [
  {
    quote: "IP Genius cut our survey prep time by 70%. We passed our Joint Commission survey with zero IC deficiencies.",
    author: "Jennifer Martinez, RN, CIC®",
    role: "Director of Infection Prevention",
    facility: "Regional Medical Center",
    rating: 5
  },
  {
    quote: "The AI document generator alone saves me 10+ hours per week. It's like having an extra IP on staff.",
    author: "David Thompson, MPH",
    role: "Infection Preventionist",
    facility: "Community Hospital Network",
    rating: 5
  },
  {
    quote: "Finally, a platform built by IPs who understand our workflow. The mock survey feature is a game-changer.",
    author: "Dr. Amanda Lee, CIC®, PhD",
    role: "Chief Quality Officer",
    facility: "University Health System",
    rating: 5
  }
];

// Facility types for mock survey demo
const facilityTypes = [
  "Acute Care Hospital",
  "Long-Term Care / SNF",
  "Ambulatory Surgical Center",
  "Dialysis Center",
  "Rehabilitation Facility"
];

export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [yearlyBilling, setYearlyBilling] = useState(true);
  const [selectedFacility, setSelectedFacility] = useState("Acute Care Hospital");
  const [chatMessage, setChatMessage] = useState("");
  const [mockSurveyResults, setMockSurveyResults] = useState(null);
  const [activeFeatureTab, setActiveFeatureTab] = useState(0);
  
  // Animated counters
  const [counters, setCounters] = useState({ hours: 0, passRate: 0, facilities: 0 });
  
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setCounters({
        hours: Math.floor(10000 * progress),
        passRate: Math.min(99.4, (99.4 * progress).toFixed(1)),
        facilities: Math.floor(500 * progress)
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    
    return () => clearInterval(timer);
  }, []);

  // Generate mock survey questions
  const generateMockSurvey = () => {
    const questions = {
      "Acute Care Hospital": [
        "Describe your process for CLABSI bundle compliance monitoring",
        "How do you track and report SSI rates post-discharge?",
        "Show me your hand hygiene observation documentation",
        "Walk me through your C. diff isolation protocol",
        "How are antimicrobial stewardship rounds documented?"
      ],
      "Long-Term Care / SNF": [
        "How do you monitor for UTI vs. asymptomatic bacteriuria?",
        "Describe your respiratory illness outbreak response plan",
        "Show me your antibiotic stewardship program documentation",
        "How are new admissions screened for MDRO colonization?",
        "Walk me through your wound care infection tracking"
      ],
      "Ambulatory Surgical Center": [
        "Describe your instrument sterilization monitoring process",
        "How do you track post-operative SSI rates?",
        "Show me your injection safety compliance records",
        "Walk me through your environmental cleaning verification",
        "How is high-level disinfection of scopes documented?"
      ],
      "Dialysis Center": [
        "Describe your vascular access infection surveillance",
        "How do you monitor water treatment system compliance?",
        "Show me your Hepatitis B vaccination tracking",
        "Walk me through your bloodborne pathogen exposure protocol",
        "How are machine disinfection cycles documented?"
      ],
      "Rehabilitation Facility": [
        "Describe your MDRO screening process for new admissions",
        "How do you track device-associated infections in rehab patients?",
        "Show me your wound care infection monitoring",
        "Walk me through your respiratory infection surveillance",
        "How is compliance with contact precautions monitored?"
      ]
    };
    
    setMockSurveyResults(questions[selectedFacility] || questions["Acute Care Hospital"]);
  };

  const featureHighlights = [
    { title: "Live IP Consultation", description: "Connect with certified IPs instantly", icon: MessageCircle },
    { title: "AI Document Engine", description: "Generate compliant docs in seconds", icon: FileText },
    { title: "Scorecard Analytics", description: "Real-time HAI tracking", icon: BarChart3 },
    { title: "Mock Survey Tool", description: "Prepare for any survey", icon: ClipboardCheck }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Sticky Navigation */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-slate-900/95 border-b border-slate-800"
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 3L4 7v6c0 5.5 3.5 10 8 11 4.5-1 8-5.5 8-11V7l-8-4z" />
                  <path d="M6 12h4l2-3 2 5 2-2h4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  nikosafe
                </span>
                <span className="text-[10px] text-emerald-400 -mt-1 tracking-wider">
                  infectionprevention
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8">
              <a href="#features" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-features">Features</a>
              <a href="#consultation" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-consultation">Live IPs</a>
              <a href="#pricing" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
              <div className="flex items-center gap-2 text-emerald-400">
                <Circle className="w-2 h-2 fill-emerald-400" />
                <span className="text-sm font-medium">14 IPs Online Now</span>
              </div>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden sm:flex items-center gap-3">
              <Link to="/login" data-testid="nav-login-btn">
                <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                  Log In
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-trial-btn">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20">
                  Start Free Trial
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="lg:hidden p-2 text-slate-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-slate-900 border-t border-slate-800"
            >
              <div className="px-4 py-6 space-y-4">
                <a href="#features" className="block text-slate-300 hover:text-white py-2">Features</a>
                <a href="#consultation" className="block text-slate-300 hover:text-white py-2">Live IPs</a>
                <a href="#pricing" className="block text-slate-300 hover:text-white py-2">Pricing</a>
                <div className="pt-4 space-y-3">
                  <Link to="/login" className="block">
                    <Button variant="outline" className="w-full border-slate-700 text-slate-300">Log In</Button>
                  </Link>
                  <Link to="/register" className="block">
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700">Start Free Trial</Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 bg-gradient-to-b from-slate-900 via-slate-900 to-emerald-950 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(16, 185, 129, 0.15) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* Overline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="uppercase tracking-[0.2em] text-xs font-bold text-emerald-400" data-testid="hero-overline">
                The Intelligent Command Center for Infection Preventionists
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              data-testid="hero-headline"
            >
              Compliance Confidence.
              <br />
              <span className="bg-gradient-to-r from-emerald-400 to-emerald-200 bg-clip-text text-transparent">
                Zero Deficiencies.
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p 
              className="mt-6 text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              data-testid="hero-subheadline"
            >
              Track infections, generate AI-powered documents, run mock surveys, and connect with certified IPs—all in one platform built by Infection Preventionists.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Link to="/register" data-testid="hero-cta-trial">
                <Button size="lg" className="h-14 px-8 text-base bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl shadow-emerald-600/30 transition-all hover:scale-105">
                  Start 14-Day Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="#consultation" data-testid="hero-cta-consult">
                <Button size="lg" variant="outline" className="h-14 px-8 text-base border-2 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-400">
                  <Video className="w-5 h-5 mr-2" />
                  Consult Live IP Now
                </Button>
              </a>
            </motion.div>

            <motion.p 
              className="mt-4 text-sm text-slate-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              No credit card required • HIPAA compliant • SOC 2 certified
            </motion.p>
          </div>

          {/* Live Stats Counter */}
          <motion.div 
            className="mt-16 sm:mt-20 grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className="text-center" data-testid="stat-hours">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {counters.hours.toLocaleString()}+
              </div>
              <div className="mt-2 text-sm text-slate-400">Hours Saved</div>
            </div>
            <div className="text-center" data-testid="stat-pass-rate">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-emerald-400" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {counters.passRate}%
              </div>
              <div className="mt-2 text-sm text-slate-400">Survey Pass Rate</div>
            </div>
            <div className="text-center" data-testid="stat-facilities">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {counters.facilities}+
              </div>
              <div className="mt-2 text-sm text-slate-400">Facilities</div>
            </div>
          </motion.div>

          {/* Product Preview Card */}
          <motion.div 
            className="mt-16 sm:mt-20 relative"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
          >
            <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl p-4 sm:p-6 max-w-5xl mx-auto">
              <img 
                src="https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?w=1200&h=600&fit=crop"
                alt="nikosafe Dashboard Preview"
                className="rounded-xl w-full object-cover opacity-90"
                data-testid="hero-preview-image"
              />
              <div className="absolute bottom-8 right-8 hidden sm:block">
                <Button className="bg-white text-slate-900 hover:bg-slate-100 shadow-lg">
                  <Play className="w-4 h-4 mr-2" />
                  Watch Demo
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust & Compliance Seals Banner */}
      <section className="py-12 sm:py-16 bg-white border-y border-slate-200" data-testid="trust-badges-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-medium text-slate-500 mb-8 uppercase tracking-wider">
            Aligned with Leading Healthcare Standards
          </p>
          <div className="relative overflow-hidden">
            <motion.div 
              className="flex gap-12 sm:gap-16"
              animate={{ x: [0, -1200] }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            >
              {[...trustBadges, ...trustBadges, ...trustBadges].map((badge, i) => (
                <div key={i} className="flex-shrink-0 text-center min-w-[140px]">
                  <div className="w-16 h-16 mx-auto bg-slate-100 rounded-xl flex items-center justify-center mb-3">
                    <span className="text-lg font-bold text-slate-700">{badge.name}</span>
                  </div>
                  <p className="text-xs text-slate-500 whitespace-nowrap">{badge.subtitle}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Carousel / Tab Section */}
      <section id="features" className="py-20 sm:py-28 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-emerald-600">
              Powerful Features
            </span>
            <h2 
              className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              data-testid="features-headline"
            >
              Everything You Need to Excel
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              Built by IPs, for IPs. Every feature designed to make your job easier and keep you survey-ready.
            </p>
          </div>

          {/* Feature Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {featureHighlights.map((feature, i) => (
              <button
                key={i}
                onClick={() => setActiveFeatureTab(i)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  activeFeatureTab === i 
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20' 
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-emerald-300'
                }`}
                data-testid={`feature-tab-${i}`}
              >
                <feature.icon className="w-4 h-4" />
                {feature.title}
              </button>
            ))}
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {bentoFeatures.map((feature, i) => (
              <motion.div
                key={feature.id}
                className={`group bg-white border border-slate-200 hover:border-emerald-400/50 rounded-2xl p-6 sm:p-8 transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/5 ${
                  feature.size === 'large' ? 'md:col-span-2 lg:col-span-1 lg:row-span-2' : ''
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`bento-card-${feature.id}`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                  feature.color === 'emerald' ? 'bg-emerald-100 text-emerald-600' :
                  feature.color === 'mint' ? 'bg-emerald-50 text-emerald-500' :
                  feature.color === 'amber' ? 'bg-amber-100 text-amber-600' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 
                  className="text-lg sm:text-xl font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                <div className="mt-4 flex items-center text-emerald-600 text-sm font-medium group-hover:translate-x-1 transition-transform">
                  Learn more <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Live IP Consultation Section */}
      <section id="consultation" className="py-20 sm:py-28 bg-slate-900" data-testid="consultation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-bold text-emerald-400">
                Live Expert Support
              </span>
              <h2 
                className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                data-testid="consultation-headline"
              >
                Connect with Certified IPs
                <span className="text-emerald-400"> Instantly</span>
              </h2>
              <p className="mt-6 text-lg text-slate-400 leading-relaxed">
                Get immediate guidance from board-certified Infection Preventionists (CIC®) for outbreak response, 
                survey prep, policy questions, and more. No waiting for callbacks.
              </p>
              
              <div className="mt-8 space-y-4">
                {[
                  "1-on-1 chat or video consultation",
                  "All consultants are CIC® certified",
                  "Available 24/7 for Enterprise plans",
                  "Secure HIPAA-compliant platform"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Live Chat Simulator Widget */}
            <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-6 sm:p-8" data-testid="live-chat-widget">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Available IP Consultants
                </h3>
                <div className="flex items-center gap-2 text-emerald-400 text-sm">
                  <Circle className="w-2 h-2 fill-emerald-400" />
                  3 Online Now
                </div>
              </div>

              <div className="space-y-4 mb-6">
                {ipConsultants.map((ip, i) => (
                  <div 
                    key={i} 
                    className="flex items-center gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-700 hover:border-emerald-500/50 transition-colors cursor-pointer"
                    data-testid={`ip-consultant-${i}`}
                  >
                    <div className="relative">
                      <img src={ip.image} alt={ip.name} className="w-12 h-12 rounded-full object-cover" />
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-slate-800 ${
                        ip.status === 'online' ? 'bg-emerald-500' : 'bg-amber-500'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium">{ip.name}</div>
                      <div className="text-sm text-slate-400">{ip.specialty}</div>
                    </div>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Quick Inquiry Input */}
              <div className="relative">
                <Input
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  placeholder="Type a quick question..."
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 pr-12"
                  data-testid="chat-input"
                />
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-emerald-600 hover:bg-emerald-700 rounded-lg flex items-center justify-center transition-colors"
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
              <p className="mt-3 text-xs text-slate-500 text-center">
                Start chatting now • First consultation free
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mock Survey Simulator Demo */}
      <section className="py-20 sm:py-28 bg-emerald-950" data-testid="mock-survey-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-emerald-400">
              Interactive Demo
            </span>
            <h2 
              className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              data-testid="mock-survey-headline"
            >
              Try the Mock Survey Generator
            </h2>
            <p className="mt-4 text-lg text-emerald-200/70 max-w-2xl mx-auto">
              Select your facility type and see sample audit questions a surveyor might ask.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-slate-900/80 backdrop-blur-xl border border-emerald-500/20 rounded-2xl p-6 sm:p-8" data-testid="mock-survey-widget">
              {/* Facility Type Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-3">Select Facility Type</label>
                <div className="flex flex-wrap gap-2">
                  {facilityTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => {
                        setSelectedFacility(type);
                        setMockSurveyResults(null);
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedFacility === type 
                          ? 'bg-emerald-600 text-white' 
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                      data-testid={`facility-btn-${type.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <Button 
                onClick={generateMockSurvey}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                data-testid="generate-survey-btn"
              >
                <Zap className="w-5 h-5 mr-2" />
                Generate 5-Point Audit Checklist
              </Button>

              {/* Results */}
              <AnimatePresence>
                {mockSurveyResults && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-6"
                  >
                    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                      <div className="flex items-center gap-3 mb-4">
                        <ClipboardCheck className="w-5 h-5 text-emerald-400" />
                        <h4 className="text-white font-semibold">Sample Survey Questions</h4>
                      </div>
                      <ol className="space-y-3">
                        {mockSurveyResults.map((question, i) => (
                          <motion.li 
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className="flex gap-3 text-slate-300"
                            data-testid={`survey-question-${i}`}
                          >
                            <span className="flex-shrink-0 w-6 h-6 bg-emerald-600/20 text-emerald-400 rounded-full flex items-center justify-center text-sm font-medium">
                              {i + 1}
                            </span>
                            <span className="leading-relaxed">{question}</span>
                          </motion.li>
                        ))}
                      </ol>
                      <p className="mt-6 text-sm text-slate-500 text-center">
                        Full version generates 50+ questions with corrective action templates
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 sm:py-28 bg-slate-50" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-emerald-600">
              Pricing Plans
            </span>
            <h2 
              className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              data-testid="pricing-headline"
            >
              Simple, Transparent Pricing
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Start free, upgrade when you're ready. No hidden fees.
            </p>

            {/* Billing Toggle */}
            <div className="mt-8 flex items-center justify-center gap-4">
              <span className={`text-sm font-medium ${!yearlyBilling ? 'text-slate-900' : 'text-slate-500'}`}>Monthly</span>
              <Switch
                checked={yearlyBilling}
                onCheckedChange={setYearlyBilling}
                className="data-[state=checked]:bg-emerald-600"
                data-testid="pricing-toggle-annual"
              />
              <span className={`text-sm font-medium ${yearlyBilling ? 'text-slate-900' : 'text-slate-500'}`}>
                Yearly <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-0">Save 20%</Badge>
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card 
                  className={`h-full bg-white border-2 ${
                    plan.popular 
                      ? 'border-emerald-500 shadow-xl shadow-emerald-500/10' 
                      : 'border-slate-200'
                  }`}
                  data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <CardContent className="p-6 sm:p-8">
                    {plan.popular && (
                      <Badge className="mb-4 bg-emerald-600 text-white border-0">Most Popular</Badge>
                    )}
                    <h3 
                      className="text-xl font-bold text-slate-900"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {plan.name}
                    </h3>
                    <p className="mt-2 text-sm text-slate-500">{plan.description}</p>
                    <div className="mt-6 mb-6">
                      <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        ${yearlyBilling ? plan.yearlyPrice : plan.monthlyPrice}
                      </span>
                      <span className="text-slate-500">/month</span>
                      {yearlyBilling && (
                        <p className="text-sm text-emerald-600 mt-1">Billed annually</p>
                      )}
                    </div>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, j) => (
                        <li key={j} className="flex items-start gap-3 text-slate-600">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/register">
                      <Button 
                        className={`w-full h-12 ${
                          plan.popular 
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                        }`}
                        data-testid={`pricing-cta-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {plan.cta}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 sm:py-28 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-emerald-600">
              Testimonials
            </span>
            <h2 
              className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              data-testid="testimonials-headline"
            >
              Trusted by Infection Preventionists
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card className="h-full bg-slate-50 border-0" data-testid={`testimonial-card-${i}`}>
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, j) => (
                        <Star key={j} className="w-5 h-5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-slate-700 leading-relaxed mb-6">"{testimonial.quote}"</p>
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.author}</p>
                      <p className="text-sm text-slate-500">{testimonial.role}</p>
                      <p className="text-sm text-emerald-600">{testimonial.facility}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28 bg-emerald-950" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            data-testid="final-cta-headline"
          >
            Ready to Transform Your
            <br />
            <span className="text-emerald-400">Infection Control Program?</span>
          </h2>
          <p className="mt-6 text-lg text-emerald-200/70 max-w-2xl mx-auto">
            Join hundreds of Infection Preventionists who trust nikosafe to stay compliant and save hours every week.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" data-testid="final-cta-trial">
              <Button size="lg" className="h-14 px-10 text-base bg-white text-emerald-900 hover:bg-slate-100 font-semibold">
                Start Your Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="#consultation" data-testid="final-cta-chat">
              <Button size="lg" variant="outline" className="h-14 px-10 text-base border-2 border-emerald-400/50 text-emerald-400 hover:bg-emerald-400/10">
                <MessageCircle className="w-5 h-5 mr-2" />
                Chat with an IP
              </Button>
            </a>
          </div>

          {/* Newsletter Signup */}
          <div className="mt-16 max-w-md mx-auto">
            <p className="text-sm text-emerald-300/70 mb-4">Get infection prevention alerts & updates</p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                className="bg-slate-900/50 border-emerald-500/30 text-white placeholder:text-slate-500"
                data-testid="newsletter-email-input"
              />
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-6" data-testid="newsletter-subscribe-btn">
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-12 sm:py-16" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center">
                  <NikosafeLogo className="w-5 h-5 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    nikosafe
                  </span>
                  <span className="text-[10px] text-emerald-400 -mt-1">infectionprevention</span>
                </div>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                The intelligent command center for Infection Preventionists. Built by IPs, for IPs.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3">
                <li><a href="#features" className="text-sm text-slate-400 hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-sm text-slate-400 hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">Integrations</a></li>
                <li><a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">API Docs</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">HIPAA Compliance</a></li>
                <li><a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">Security</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © 2026 nikosafe. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span>HIPAA Compliant</span>
              <span>SOC 2 Type II</span>
              <span>99.9% Uptime SLA</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
